import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { Users, Plus, Search, Filter } from 'lucide-react';
import { cn } from '@/lib/utils';

interface Group {
  id: string;
  name: string;
  description: string;
  avatar_url: string;
  cover_image: string;
  visibility: 'public' | 'private' | 'group';
  requires_approval: boolean;
  members_count: number;
  creator: {
    id: string;
    full_name: string;
    avatar_url: string;
  };
}

export function GroupsPage() {
  const navigate = useNavigate();
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'my-groups' | 'public'>('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadGroups();
  }, [filter]);

  const loadGroups = async () => {
    try {
      let query = supabase
        .from('groups')
        .select(`
          *,
          creator:profiles!creator_id(*),
          members:group_members(count)
        `);

      // Apply filters
      if (filter === 'my-groups') {
        query = query.in('id', supabase
          .from('group_members')
          .select('group_id')
          .eq('user_id', supabase.auth.user()?.id)
        );
      } else if (filter === 'public') {
        query = query.eq('visibility', 'public');
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) throw error;
      setGroups(data || []);
    } catch (error) {
      console.error('Error loading groups:', error);
      setError('Failed to load groups');
    } finally {
      setLoading(false);
    }
  };

  const filteredGroups = groups.filter(group => {
    if (!searchQuery) return true;
    const search = searchQuery.toLowerCase();
    return (
      group.name.toLowerCase().includes(search) ||
      group.description.toLowerCase().includes(search) ||
      group.creator.full_name.toLowerCase().includes(search)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-royal-purple"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Groups</h1>
        <button
          onClick={() => navigate('/groups/create')}
          className="flex items-center gap-2 px-4 py-2 bg-royal-purple text-white rounded-lg hover:bg-royal-purple/90 transition-colors"
        >
          <Plus className="w-5 h-5" />
          <span>Create Group</span>
        </button>
      </div>

      {/* Search and filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search groups..."
            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-royal-purple focus:border-transparent"
          />
        </div>
        <div className="flex gap-2">
          {['all', 'my-groups', 'public'].map((option) => (
            <button
              key={option}
              onClick={() => setFilter(option as typeof filter)}
              className={cn(
                "px-4 py-2 rounded-lg font-medium transition-colors",
                filter === option
                  ? "bg-royal-purple text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              )}
            >
              {option.split('-').map(word => 
                word.charAt(0).toUpperCase() + word.slice(1)
              ).join(' ')}
            </button>
          ))}
        </div>
      </div>

      {/* Groups grid */}
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {filteredGroups.map((group) => (
          <button
            key={group.id}
            onClick={() => navigate(`/groups/${group.id}`)}
            className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow overflow-hidden text-left group"
          >
            {/* Cover image */}
            <div className="relative h-48">
              <img
                src={group.cover_image || 'https://images.unsplash.com/photo-1523580494863-6f3031224c94?auto=format&fit=crop&q=80'}
                alt={group.name}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4">
                <h3 className="text-lg font-semibold text-white line-clamp-2">
                  {group.name}
                </h3>
              </div>
            </div>

            {/* Group details */}
            <div className="p-4 space-y-4">
              <p className="text-sm text-gray-600 line-clamp-2">
                {group.description}
              </p>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-gray-600" />
                  <span className="text-sm text-gray-600">
                    {group.members_count || 0} members
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <img
                    src={group.creator.avatar_url}
                    alt={group.creator.full_name}
                    className="w-6 h-6 rounded-full"
                  />
                  <span className="text-sm text-gray-600">
                    {group.creator.full_name}
                  </span>
                </div>
              </div>

              {group.requires_approval && (
                <div className="text-sm text-gray-500">
                  Requires approval to join
                </div>
              )}
            </div>
          </button>
        ))}
      </div>

      {filteredGroups.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500">No groups found</p>
        </div>
      )}
    </div>
  );
}